import { RefObject, useCallback, useEffect } from 'react';

import { LOCATION_ICON_PAC_INLINE } from '../components/Icons/LocationIcon';
import { PORTAL_POPOVER_Z } from '../constants/modalZIndex';

function applyPacLocationIcons(pac: HTMLElement) {
  pac.querySelectorAll<HTMLElement>('.pac-icon').forEach(el => {
    if (el.querySelector('svg[data-nc-loc]')) return;
    el.style.backgroundImage = 'none';
    el.style.backgroundPosition = 'initial';
    el.style.backgroundSize = 'initial';
    el.innerHTML = LOCATION_ICON_PAC_INLINE;
  });
}

const MOBILE_BREAKPOINT = 768;
const VIEWPORT_PADDING = 12;
const PAC_GAP = 4;

function isPacInputActive(input: HTMLElement): boolean {
  const active = document.activeElement;
  if (!active) return false;
  if (active === input) return true;
  const outlineRoot = input.closest('.MuiOutlinedInput-root');
  if (outlineRoot?.contains(active)) return true;
  const pac = document.querySelector('.pac-container');
  return Boolean(pac?.contains(active));
}

function getMaxPacHeight(): number {
  return window.innerWidth <= MOBILE_BREAKPOINT
    ? Math.min(240, Math.max(160, window.innerHeight * 0.42))
    : 240;
}

export function usePacContainerWidthSync(inputRef: RefObject<HTMLElement | null>, enabled: boolean) {
  const sync = useCallback(() => {
    const input = inputRef.current;
    if (!input || !isPacInputActive(input)) return;
    const outlineRoot = input.closest('.MuiOutlinedInput-root') as HTMLElement | null;
    const widthEl = outlineRoot ?? input;
    const rootRect = widthEl.getBoundingClientRect();
    const inputRect = input.getBoundingClientRect();
    const w = Math.round(rootRect.width);
    if (w <= 0) return;

    const maxPacHeight = getMaxPacHeight();
    const spaceBelow = window.innerHeight - VIEWPORT_PADDING - rootRect.bottom - PAC_GAP;
    const spaceAbove = rootRect.top - VIEWPORT_PADDING - PAC_GAP;
    const preferOpenAbove = window.innerWidth <= MOBILE_BREAKPOINT;
    const openAbove =
      preferOpenAbove || (spaceBelow < maxPacHeight && spaceAbove > spaceBelow);

    const visiblePacs = Array.from(document.querySelectorAll<HTMLElement>('.pac-container')).filter(pac => {
      const s = window.getComputedStyle(pac);
      if (s.display === 'none' || s.visibility === 'hidden') return false;
      return pac.getBoundingClientRect().height > 0 || pac.childElementCount > 0;
    });

    const inputRow = inputRect;
    const matchesThisField = visiblePacs.filter(pac => {
      const pr = pac.getBoundingClientRect();
      const horizNear = Math.abs(pr.left - inputRow.left) < 96;
      const belowField = pr.top >= inputRow.top - 12 && pr.top <= inputRow.bottom + maxPacHeight + 24;
      const aboveField =
        pr.bottom <= inputRow.bottom + 12 && pr.bottom >= inputRow.top - maxPacHeight - 24;
      return horizNear && (belowField || aboveField);
    });

    let targets = matchesThisField;
    if (targets.length === 0 && visiblePacs.length === 1) {
      targets = visiblePacs;
    } else if (targets.length === 0 && visiblePacs.length > 1) {
      const anchorY = openAbove ? rootRect.top : rootRect.bottom;
      targets = [
        visiblePacs.reduce((closest, pac) => {
          const pacY = pac.getBoundingClientRect().top;
          const closestY = closest.getBoundingClientRect().top;
          return Math.abs(pacY - anchorY) < Math.abs(closestY - anchorY) ? pac : closest;
        }),
      ];
    }

    targets.forEach(pac => {
      const pacHeight = Math.min(Math.max(pac.scrollHeight, pac.offsetHeight), maxPacHeight) || maxPacHeight;

      pac.style.boxSizing = 'border-box';
      pac.style.width = `${w}px`;
      pac.style.minWidth = `${w}px`;
      pac.style.position = 'fixed';
      pac.style.left = `${Math.round(rootRect.left)}px`;
      pac.style.right = 'auto';
      pac.style.transform = 'none';
      pac.style.zIndex = String(PORTAL_POPOVER_Z);
      pac.style.maxHeight = `${maxPacHeight}px`;
      pac.style.overflowY = 'auto';

      if (openAbove) {
        const top = Math.max(VIEWPORT_PADDING, rootRect.top - pacHeight - PAC_GAP);
        pac.style.top = `${Math.round(top)}px`;
        pac.style.bottom = 'auto';
        pac.style.marginTop = '0';
        pac.style.marginBottom = `${PAC_GAP}px`;
        pac.classList.add('pac-container--above');
      } else {
        const top = Math.min(
          window.innerHeight - VIEWPORT_PADDING - pacHeight,
          rootRect.bottom + PAC_GAP
        );
        pac.style.top = `${Math.round(Math.max(VIEWPORT_PADDING, top))}px`;
        pac.style.bottom = 'auto';
        pac.style.marginTop = `${PAC_GAP}px`;
        pac.style.marginBottom = '0';
        pac.classList.remove('pac-container--above');
      }

      applyPacLocationIcons(pac);
    });
  }, [inputRef]);

  useEffect(() => {
    if (!enabled) return;
    const input = inputRef.current;
    if (!input) return;

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(sync);
    };

    input.addEventListener('input', schedule);
    input.addEventListener('focus', schedule);
    input.addEventListener('keydown', schedule);
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', schedule, true);

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(raf);
      input.removeEventListener('input', schedule);
      input.removeEventListener('focus', schedule);
      input.removeEventListener('keydown', schedule);
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', schedule, true);
      observer.disconnect();
    };
  }, [enabled, inputRef, sync]);
}
